import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/view/components/ui/use-toast'
import { authService } from '@/app/services/authService'
import { useRef, useState } from 'react'

interface UseCodeRecoveryControllerProps {
  cpf: string
  onTokenSet: (token: string) => void
}

export const useCodeRecoveryController = ({
  cpf,
  onTokenSet,
}: UseCodeRecoveryControllerProps) => {
  const codeLength = 6
  const [code, setCode] = useState<string[]>(Array(codeLength).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    Array(codeLength).fill(null),
  )
  const { toast } = useToast()
  const navigate = useNavigate()

  const handleCodeInput = (text: string, index: number) => {
    const newCode = [...code]
    newCode[index] = text
    setCode(newCode)

    if (text) {
      if (index < codeLength - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    } else {
      if (index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    }
  }

  const handleSendCode = async () => {
    if (!cpf) return
    const request = await authService.forgotPassword({ cpf })
    if (request.success) {
      toast({
        title: 'Código re-enviado para o email.',
      })
    }
  }

  const isCodeEntered = code.every((value) => value !== '')

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ cpf, code }: { cpf: string; code: string }) => {
      return authService.recoveryCode({ cpf, code })
    },
  })

  const handleFormSubmit = async () => {
    try {
      const stringCode = code.join('')

      if (!cpf) {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: 'CPF não encontrado. Volte para a tela anterior.',
        })
        return
      }

      if (!isCodeEntered) {
        toast({
          variant: 'destructive',
          title: 'Código incompleto',
          description: 'Digite o código completo de 6 dígitos.',
        })
        return
      }

      if (stringCode.length !== 6) {
        toast({
          variant: 'destructive',
          title: 'Código inválido',
          description: 'O código deve conter exatamente 6 dígitos.',
        })
        return
      }

      const response = await mutateAsync({ cpf, code: stringCode })

      onTokenSet(response.token)

      toast({
        title: 'Código verificado!',
        description: 'Código válido. Você pode definir sua nova senha.',
      })

      navigate('/new-password')
    } catch (error) {
      console.error('Error submitting code:', error)
      toast({
        variant: 'destructive',
        title: 'Código inválido',
        description: 'Verifique o código informado e tente novamente.',
      })
    }
  }

  return {
    isPending,
    handleFormSubmit,
    handleCodeInput,
    handleSendCode,
    isCodeEntered,
    code,
    inputRefs,
  }
}
